import React, { FC, Fragment } from 'react';
import { Stack, Text } from '@moda/om';
import { formatNumber } from '@moda/portal-stanchions';
import { useReturnSummary } from '../../../hooks/useReturnSummary';
import { PaymentSummaryRow } from '../PaymentSummaryRow';

import './RefundSummary.scss';
import { ReturnMethod } from '../../../constants';

interface Props {
  shipmentNumber?: string;
  orderNumber?: string;
}

export const RefundSummary: FC<Props> = ({ shipmentNumber, orderNumber }) => {
  const { returnSummary } = useReturnSummary({ shipmentNumber, orderNumber });

  if (!returnSummary) return null;

  const {
    attributes: {
      currencyUnit,
      refundDetails,
      returnDetails,
      returnSiteCreditBonusAmount,
      returnMethodId,
      returnFeeDetails: { returnShippingFee, message: returnFeeMessage },
      totalRefundAmount,
      itemRefundAmount,
      taxes,
      taxDetails: { taxType, refundable }
    },
    type
  } = returnSummary;

  const detailList = type === 'request' && refundDetails ? refundDetails : returnDetails;

  return (
    <Stack space={3}>
      <Stack space={4} className="RefundSummary">
        <Text treatment="h5" family="serif">
          Refund Summary
        </Text>

        {returnFeeMessage && <Text>{returnFeeMessage}</Text>}

        <Stack space={3}>
          <PaymentSummaryRow
            label="Item Refund"
            value={
              <>
                {currencyUnit}
                {formatNumber(itemRefundAmount)}
              </>
            }
          />

          <PaymentSummaryRow
            label={taxType}
            value={
              <>
                {currencyUnit}
                {refundable ? formatNumber(taxes) : 'Non-Refundable'}
              </>
            }
          />

          {returnMethodId === ReturnMethod.SiteCredit &&
            Number(returnSiteCreditBonusAmount) > 0 && (
              <PaymentSummaryRow
                label="Site Credit Bonus"
                value={
                  <>
                    {currencyUnit}
                    {formatNumber(returnSiteCreditBonusAmount)}
                  </>
                }
              />
            )}

          {Number(returnShippingFee) > 0 && (
            <PaymentSummaryRow
              label="Return Shipping Fee"
              value={
                <>
                  - {currencyUnit}
                  {formatNumber(returnShippingFee)}
                </>
              }
            />
          )}
        </Stack>

        <div className="RefundSummary__total">
          <PaymentSummaryRow
            label="Total Refund"
            value={
              <>
                {currencyUnit}
                {formatNumber(totalRefundAmount)}
              </>
            }
          />
        </div>

        {detailList.map(({ paymentMethod, amount }, index) => (
          <Fragment key={index}>
            {Number(amount) > 0 && (
              <PaymentSummaryRow
                key={index}
                label={paymentMethod}
                value={
                  <>
                    {currencyUnit}
                    {formatNumber(amount)}
                  </>
                }
              />
            )}
          </Fragment>
        ))}
      </Stack>
      <Text color="cement">*Initial shipping charges are non-refundable</Text>
    </Stack>
  );
};
